import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import {
  Plus,
  Search,
  ChevronRight,
  Sparkles,
  ChevronLeft,
  Trash2,
  Check,
  X,
  BookOpen,
  Info,
  Edit2,
  Layers,
  Zap,
  HelpCircle,
  BrainCircuit,
} from "lucide-react";
import apiClient from "../utils/api";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import PageHeader from "../components/PageHeader";
import { useToast } from "../context/ToastContext";

const KnowledgeBase = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("questions"); // 'questions' or 'recommendations'
  const [currentPage, setCurrentPage] = useState(1);
  const QUESTIONS_PER_PAGE = 10;

  // New Question / Generate
  const [newQuestion, setNewQuestion] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [recommendedVariations, setRecommendedVariations] = useState([]);

  // Manual Response Form State
  const [showManualResForm, setShowManualResForm] = useState(null); // questionId if showing form
  const [manualInstruction, setManualInstruction] = useState("");
  const [manualTrigger, setManualTrigger] = useState("");


  // Editing State
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [editingQuestionText, setEditingQuestionText] = useState("");

  const [editingResponseId, setEditingResponseId] = useState(null);
  const [editingInstruction, setEditingInstruction] = useState("");
  const [editingTrigger, setEditingTrigger] = useState("");

  // New Topic Modal
  const [showTopicModal, setShowTopicModal] = useState(false);
  const [topicName, setTopicName] = useState("");
  const [topicDesc, setTopicDesc] = useState("");

  const [expandedQuestions, setExpandedQuestions] = useState([]);

  // Delete Modal State
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteData, setDeleteData] = useState({
    id: null,
    type: null,
    extraId: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchTopics = React.useCallback(async () => {
    try {
      const data = await apiClient.getKBTopics();
      setTopics(data);
    } catch (err) {
      console.error("Failed to fetch topics", err);
    }
  }, []);

  const fetchQuestions = async (topicId) => {
    setLoading(true);
    try {
      const data = await apiClient.getKBQuestions(topicId);
      setQuestions(data);
    } catch (err) {
      console.error("Failed to fetch questions", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTopics();
    setSelectedTopic(null); // Reset selected topic on agent switch
  }, [activeAiAgentId, fetchTopics]);

  useEffect(() => {
    if (topics.length > 0 && !selectedTopic) {
      setSelectedTopic(topics[0]);
    }
  }, [topics, selectedTopic]);

  useEffect(() => {
    if (selectedTopic) {
      fetchQuestions(selectedTopic.id);
    }
  }, [selectedTopic]);

  const handleCreateTopic = async () => {
    if (!topicName.trim()) return;
    try {
      await apiClient.createKBTopic(topicName, topicDesc);
      setTopicName("");
      setTopicDesc("");
      setShowTopicModal(false);
      fetchTopics();
      showToast("Intelligence domain forged successfully");
    } catch (err) {
      console.error("Failed to create topic", err);
    }
  };

  const handleDeleteTopic = (e, topicId) => {
    e.stopPropagation();
    setDeleteData({ id: topicId, type: "topic" });
    setIsDeleteModalOpen(true);
  };

  const handleGenerateVariations = async () => {
    if (!newQuestion.trim() || !selectedTopic) return;
    setIsGenerating(true);
    setActiveTab("recommendations");
    try {
      const variations = await apiClient.generateFAQVariations(
        selectedTopic.name,
        newQuestion,
      );
      setRecommendedVariations(
        variations.map((qText) => ({
          question_text: qText,
          instruction_text: "",
          trigger_condition: "",
        })),
      );
    } catch (err) {
      console.error("Failed to generate variations", err);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleUpdateVariationInput = (index, field, value) => {
    setRecommendedVariations((prev) => {
      const newArr = [...prev];
      newArr[index] = { ...newArr[index], [field]: value };
      return newArr;
    });
  };

  const handleApproveVariation = async (variation, index) => {
    if (
      !variation.instruction_text.trim() ||
      !variation.trigger_condition.trim()
    ) {
      showToast(
        "Please provide both Instruction and Trigger Condition before approving.",
        "warning",
      );
      return;
    }

    try {
      const qRes = await apiClient.createKBQuestion(
        selectedTopic.id,
        variation.question_text,
      );
      const questionId = qRes.id;

      await apiClient.createKBResponse(
        questionId,
        variation.instruction_text,
        variation.trigger_condition,
      );

      setRecommendedVariations((prev) => prev.filter((_, i) => i !== index));
      fetchQuestions(selectedTopic.id);
      showToast("Variation approved and neural training sequence initiated.");
    } catch (err) {
      console.error("Failed to approve variation", err);
    }
  };

  const handleAddManualResponse = async (questionId) => {
    if (!manualInstruction.trim() || !manualTrigger.trim()) {
      showToast("Please fill in both fields.", "warning");
      return;
    }

    try {
      await apiClient.createKBResponse(
        questionId,
        manualInstruction,
        manualTrigger,
      );
      setManualInstruction("");
      setManualTrigger("");
      setShowManualResForm(null);
      fetchQuestions(selectedTopic.id);
      showToast("Manual variation committed successfully.");
    } catch (err) {
      console.error("Failed to add manual response", err);
    }
  };

  const handleDeleteQuestion = (questionId) => {
    setDeleteData({ id: questionId, type: "question" });
    setIsDeleteModalOpen(true);
  };

  const handleDeleteResponse = (responseId, questionId) => {
    setDeleteData({ id: responseId, type: "response", extraId: questionId });
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    try {
      if (deleteData.type === "topic") {
        await apiClient.deleteKBTopic(deleteData.id);
        if (selectedTopic?.id === deleteData.id) setSelectedTopic(null);
        fetchTopics();
        showToast("Intelligence domain Deleted successfully.");
      } else if (deleteData.type === "question") {
        await apiClient.deleteKBQuestion(deleteData.id);
        fetchQuestions(selectedTopic.id);
        showToast("Question sequence deleted.");
      } else if (deleteData.type === "response") {
        await apiClient.deleteKBResponse(deleteData.id);
        fetchQuestions(selectedTopic.id);
        showToast("Variation response deleted.");
      }
      setIsDeleteModalOpen(false);
    } catch (err) {
      console.error(`Failed to delete ${deleteData.type}`, err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUpdateQuestion = async (questionId) => {
    if (!editingQuestionText.trim()) return;
    try {
      await apiClient.updateKBQuestion(questionId, editingQuestionText);
      setEditingQuestionId(null);
      fetchQuestions(selectedTopic.id);
      showToast("Question sequence updated.");
    } catch (err) {
      console.error("Failed to update question", err);
    }
  };

  const handleUpdateResponse = async (responseId) => {
    if (!editingInstruction.trim() || !editingTrigger.trim()) return;
    try {
      await apiClient.updateKBResponse(
        responseId,
        editingInstruction,
        editingTrigger,
      );
      setEditingResponseId(null);
      fetchQuestions(selectedTopic.id);
      showToast("Variation logic synchronized.");
    } catch (err) {
      console.error("Failed to update response", err);
    }
  };



  const toggleQuestionExpansion = (id) => {
    setExpandedQuestions((prev) =>
      prev.includes(id) ? prev.filter((qId) => qId !== id) : [...prev, id],
    );
  };

  return (
    <div className="min-h-screen font-sans bg-[#FDFCFD] pt-4 pb-8 px-8 lg:pt-6 lg:pb-12 lg:px-12 animate-fadeIn border-t border-gray-100">
      <div className="w-full max-w-6xl mx-auto space-y-6">
        <PageHeader
          title="Smart FAQ Builder"
          description="Train your AI assistant with context-aware question and answer variations to handle customer queries with high precision."
        />

        <div className="bg-white rounded-2xl border border-gray-200 shadow-xl shadow-slate-100/50 flex h-[620px] overflow-hidden animate-slideIn">
          {/* Topics Sidebar Card */}
          <div className="w-[260px] shrink-0 border-r border-gray-100 flex flex-col h-full overflow-hidden bg-gray-50/10">
            <div className="p-4 border-b border-gray-50 bg-gray-50/30">
              <div className="flex justify-between items-center mb-1.5">
                <h3 className="text-[10px] font-bold text-gray-400 uppercase">
                  Topics
                </h3>
                <button
                  onClick={() => setShowTopicModal(true)}
                  className="w-7 h-7 bg-gray-900 text-white rounded-lg hover:bg-black transition-all shadow-md flex items-center justify-center flex-shrink-0"
                  title="Create Topic"
                >
                  <Plus size={14} />
                </button>
              </div>
              <p className="text-xs font-bold text-gray-900 tracking-tight">
                Knowledge Domains
              </p>
            </div>

            <div className="flex-1 overflow-y-auto p-3 space-y-1 custom-scrollbar">
              {topics.length === 0 ? (
                <div className="p-6 text-center bg-gray-50 rounded-xl border border-dashed border-gray-200">
                  <HelpCircle
                    size={24}
                    className="mx-auto text-gray-300 mb-2 opacity-30"
                  />
                  <p className="text-[10px] font-bold text-gray-400 uppercase">
                    No Topics Yet
                  </p>
                </div>
              ) : (
                topics.map((topic) => (
                  <div
                    key={topic.id}
                    onClick={() => setSelectedTopic(topic)}
                    className={`group p-2.5 px-4 rounded-xl cursor-pointer transition-all flex justify-between items-center ${selectedTopic?.id === topic.id
                      ? "bg-gray-900 text-white shadow-lg shadow-gray-200"
                      : "text-gray-600 hover:bg-gray-50"
                      }`}
                  >
                    <div className="flex items-center gap-2 overflow-hidden">
                      <div
                        className={`p-1.5 rounded-lg transition-colors ${selectedTopic?.id === topic.id
                          ? "bg-gray-800"
                          : "bg-gray-50 group-hover:bg-white"
                          }`}
                      >
                        <Layers size={14} />
                      </div>
                      <span className="text-xs font-bold truncate tracking-tight">
                        {topic.name}
                      </span>
                    </div>
                    <button
                      onClick={(e) => handleDeleteTopic(e, topic.id)}
                      className={`p-1.5 rounded-lg border transition-all shadow-sm flex items-center justify-center ${selectedTopic?.id === topic.id
                        ? "bg-gray-800 border-gray-700 text-gray-400 hover:text-red-400 hover:bg-gray-700"
                        : "bg-white border-gray-100 text-gray-350 hover:text-red-500 hover:bg-red-50 hover:border-red-100"
                        }`}
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0 flex flex-col h-full bg-white overflow-hidden">
            {selectedTopic ? (
              <div className="flex-1 flex flex-col h-full overflow-hidden p-5 space-y-4">
                <div className="bg-gray-50/50 border border-gray-100/30 rounded-2xl p-4 shrink-0 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-gray-900 text-white rounded-xl flex items-center justify-center shadow-md">
                      <Layers size={16} />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold tracking-wide text-gray-900 uppercase">
                        {selectedTopic.name}
                      </h3>
                      <p className="text-gray-400 text-[11px] font-medium leading-relaxed mt-0.5">
                        {selectedTopic.description ||
                          `Specialized intelligence repository for ${selectedTopic.name}.`}
                      </p>
                    </div>
                  </div>
                  <div className="flex bg-white p-0.5 rounded-xl border border-gray-100 shadow-sm">
                    <button
                      onClick={() => setActiveTab("questions")}
                      className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all duration-300 flex items-center gap-1.5 ${activeTab === "questions"
                        ? "bg-gray-900 text-white shadow-md"
                        : "text-gray-400 hover:text-gray-900"
                        }`}
                    >
                      <HelpCircle size={12} />
                      FAQ ({questions.length})
                    </button>
                    <button
                      onClick={() => setActiveTab("recommendations")}
                      className={`px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all duration-300 flex items-center gap-1.5 ${activeTab === "recommendations"
                        ? "bg-gray-900 text-white shadow-md"
                        : "text-gray-400 hover:text-gray-900"
                        }`}
                    >
                      <BrainCircuit size={12} />
                      AI Lab
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto pr-2 custom-scrollbar space-y-4 min-h-0">
                  {activeTab === "questions" ? (
                    <div className="space-y-4 animate-slideIn">
                      <div className="relative group">
                        <Search
                          size={14}
                          className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-gray-900 transition-colors"
                        />
                        <input
                          type="text"
                          placeholder="Search knowledge repository..."
                          className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:border-slate-300 focus:bg-white transition-all outline-none shadow-sm"
                        />
                      </div>

                      <div className="grid gap-3 pr-1">
                        {questions
                          .slice(
                            (currentPage - 1) * QUESTIONS_PER_PAGE,
                            currentPage * QUESTIONS_PER_PAGE,
                          )
                          .map((q) => (
                            <div
                              key={q.id}
                              className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm hover:shadow-md hover:shadow-gray-100/50 transition-all duration-300"
                            >
                              <div
                                onClick={() => toggleQuestionExpansion(q.id)}
                                className="px-5 py-3.5 flex items-center justify-between cursor-pointer group/item"
                              >
                                <div className="flex items-center gap-4 flex-1 mr-4">
                                  <div
                                    className={`p-1.5 rounded-lg transition-all ${expandedQuestions.includes(q.id) ? "bg-gray-900 text-white rotate-90" : "bg-gray-50 text-gray-400 group-hover/item:bg-gray-100"}`}
                                  >
                                    <ChevronRight size={16} />
                                  </div>

                                  {editingQuestionId === q.id ? (
                                    <div className="flex items-start gap-3 flex-1">
                                      <textarea
                                        value={editingQuestionText}
                                        onChange={(e) =>
                                          setEditingQuestionText(e.target.value)
                                        }
                                        autoFocus
                                        className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 text-gray-900 text-xs leading-relaxed resize-none h-16 transition-all"
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <div className="flex flex-col gap-1.5 pt-1">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleUpdateQuestion(q.id);
                                          }}
                                          className="p-1.5 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200"
                                        >
                                          <Check size={16} />
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setEditingQuestionId(null);
                                          }}
                                          className="p-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100"
                                        >
                                          <X size={16} />
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex flex-col">
                                      <span className="text-xs font-bold text-gray-900 leading-tight mb-1">
                                        {q.text}
                                      </span>
                                      <div className="flex items-center gap-2">
                                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium tracking-normal bg-gray-50 text-gray-400 border border-gray-100">
                                          {q.responses?.length || 0} variations
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </div>
                                <div className="flex gap-1.5 opacity-0 group-hover/item:opacity-100 transition-opacity">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingQuestionId(q.id);
                                      setEditingQuestionText(q.text);
                                      if (!expandedQuestions.includes(q.id))
                                        toggleQuestionExpansion(q.id);
                                    }}
                                    className="p-1.5 text-gray-400 bg-white border border-gray-100 hover:text-gray-900 hover:bg-gray-50 hover:border-gray-200 rounded-lg transition-all shadow-sm flex items-center justify-center"
                                  >
                                    <Edit2 size={14} />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteQuestion(q.id);
                                    }}
                                    className="p-1.5 text-gray-400 bg-white border border-gray-100 hover:text-red-500 hover:bg-red-50 hover:border-red-100 rounded-lg transition-all shadow-sm flex items-center justify-center"
                                  >
                                    <Trash2 size={14} />
                                  </button>
                                </div>
                              </div>

                              {expandedQuestions.includes(q.id) && (
                                <div className="px-5 pb-5 pt-1.5 bg-gray-50/30 border-t border-gray-50 animate-fadeIn ml-10 mr-4 mb-4 rounded-xl">
                                  <div className="space-y-3">
                                    {q.responses?.map((resp) => (
                                      <div
                                        key={resp.id}
                                        className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm group/resp relative"
                                      >
                                        <div className="flex justify-between items-start gap-4">
                                          <div className="flex-1">
                                            <div className="text-[10px] font-bold text-gray-400 uppercase mb-2 flex items-center gap-1.5">
                                              <Info
                                                size={10}
                                                className="text-gray-400"
                                              />
                                              Variation Logic
                                            </div>
                                            {editingResponseId === resp.id ? (
                                              <div className="space-y-3">
                                                <div className="relative">
                                                  <textarea
                                                    value={editingInstruction}
                                                    onChange={(e) =>
                                                      setEditingInstruction(
                                                        e.target.value,
                                                      )
                                                    }
                                                    className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 text-gray-900 text-xs leading-relaxed h-20"
                                                  />
                                                </div>
                                                <div className="space-y-1">
                                                  <label className="text-[10px] font-bold text-gray-400 uppercase ml-1">
                                                    Trigger Condition
                                                  </label>
                                                  <input
                                                    value={editingTrigger}
                                                    onChange={(e) =>
                                                      setEditingTrigger(
                                                        e.target.value,
                                                      )
                                                    }
                                                    className="w-full px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 text-gray-950 text-xs"
                                                  />
                                                </div>
                                              </div>
                                            ) : (
                                              <div className="space-y-2">
                                                <div className="bg-slate-50 border border-slate-100 p-3 rounded-lg">
                                                  <p className="text-gray-900 font-medium text-[11px] leading-relaxed">
                                                    "{resp.instruction_text}"
                                                  </p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                  <span className="text-[10px] font-bold text-gray-400 uppercase bg-gray-50 px-1.5 py-0.5 rounded">
                                                    Trigger
                                                  </span>
                                                  <p className="text-[11px] font-medium text-gray-600 truncate">
                                                    {resp.trigger_condition}
                                                  </p>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                          <div className="flex gap-1.5">
                                            {editingResponseId === resp.id ? (
                                              <>
                                                <button
                                                  onClick={() =>
                                                    handleUpdateResponse(
                                                      resp.id,
                                                    )
                                                  }
                                                  className="p-1.5 bg-slate-100 text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-200 transition-all flex items-center justify-center"
                                                >
                                                  <Check size={14} />
                                                </button>
                                                <button
                                                  onClick={() =>
                                                    setEditingResponseId(null)
                                                  }
                                                  className="p-1.5 bg-red-50 text-red-600 border border-red-100 rounded-lg hover:bg-red-100 transition-all flex items-center justify-center"
                                                >
                                                  <X size={14} />
                                                </button>
                                              </>
                                            ) : (
                                              <>
                                                <button
                                                  onClick={() => {
                                                    setEditingResponseId(
                                                      resp.id,
                                                    );
                                                    setEditingInstruction(
                                                      resp.instruction_text,
                                                    );
                                                    setEditingTrigger(
                                                      resp.trigger_condition,
                                                    );
                                                  }}
                                                  className="p-1.5 text-gray-400 bg-white border border-gray-100 hover:text-gray-900 hover:bg-gray-50 hover:border-gray-200 rounded-lg transition-all shadow-sm flex items-center justify-center"
                                                >
                                                  <Edit2 size={14} />
                                                </button>
                                                <button
                                                  onClick={() =>
                                                    handleDeleteResponse(
                                                      resp.id,
                                                      q.id,
                                                    )
                                                  }
                                                  className="p-1.5 text-gray-400 bg-white border border-gray-100 hover:text-red-500 hover:bg-red-50 hover:border-red-100 rounded-lg transition-all shadow-sm flex items-center justify-center"
                                                >
                                                  <Trash2 size={14} />
                                                </button>
                                              </>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    ))}

                                    {showManualResForm === q.id ? (
                                      <div className="bg-white border border-gray-200 rounded-2xl p-5 shadow-lg animate-scaleIn mt-3">
                                        <div className="flex items-center gap-2 mb-4">
                                          <div className="w-7 h-7 bg-gray-900 rounded-lg flex items-center justify-center text-white">
                                            <Plus size={14} />
                                          </div>
                                          <h4 className="text-sm font-bold tracking-wide text-gray-900">
                                            Add New Variation
                                          </h4>
                                        </div>
                                        <div className="space-y-4">
                                          <div className="space-y-1.5">
                                            <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                                              Instruction
                                            </label>
                                            <div className="relative">
                                              <textarea
                                                value={manualInstruction}
                                                onChange={(e) =>
                                                  setManualInstruction(
                                                    e.target.value,
                                                  )
                                                }
                                                placeholder="Enter specific instruction..."
                                                className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 text-gray-900 font-medium text-xs h-24 resize-none"
                                              />
                                              {/* AI Improve button removed */}
                                            </div>
                                          </div>
                                          <div className="space-y-1.5">
                                            <label className="text-[9px] font-bold text-gray-400 uppercase tracking-widest ml-1">
                                              Trigger Condition
                                            </label>
                                            <input
                                              value={manualTrigger}
                                              onChange={(e) =>
                                                setManualTrigger(e.target.value)
                                              }
                                              placeholder="e.g. When asking about specific status"
                                              className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gray-900 text-gray-900 font-bold text-xs"
                                            />
                                          </div>
                                          <div className="flex gap-2 pt-1">
                                            <button
                                              onClick={() =>
                                                setShowManualResForm(null)
                                              }
                                              className="flex-1 px-4 py-2 text-gray-400 hover:text-gray-900 font-bold text-xs rounded-xl"
                                            >
                                              Discard
                                            </button>
                                            <button
                                              onClick={() =>
                                                handleAddManualResponse(q.id)
                                              }
                                              className="flex-1 px-4 py-2 bg-gray-900 text-white font-bold text-xs rounded-xl hover:bg-gray-800 transition-all shadow shadow-gray-200"
                                            >
                                              Save Variation
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      <button
                                        onClick={() =>
                                          setShowManualResForm(q.id)
                                        }
                                        className="w-full py-2.5 rounded-xl border-2 border-dashed border-gray-100 text-gray-400 font-medium text-xs uppercase tracking-normal hover:border-gray-900 hover:text-gray-950 transition-all mt-3 bg-white/50"
                                      >
                                        + Add Manual Variation
                                      </button>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        {questions.length === 0 && !loading && (
                          <div className="p-8 text-center bg-white rounded-2xl border border-gray-100 shadow-sm">
                            <div className="w-10 h-10 bg-gray-50 rounded-xl flex items-center justify-center mx-auto mb-4 border border-gray-100">
                              <BookOpen size={20} className="text-gray-400" />
                            </div>
                            <h3 className="text-sm font-bold tracking-wide text-gray-900 uppercase mb-2">
                              Knowledge Base Empty
                            </h3>
                            <p className="text-gray-400 max-w-sm mx-auto mb-4 font-medium text-xs leading-normal">
                              This topic hasn't been trained yet. Use the AI
                              Idea Lab to brainstorm questions or add them
                              manually.
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Pagination Controls */}
                      {Math.ceil(questions.length / QUESTIONS_PER_PAGE) > 1 && (
                        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-50">
                          <span className="text-[10px] font-bold text-gray-400 uppercase px-2">
                            Page {currentPage} of{" "}
                            {Math.ceil(questions.length / QUESTIONS_PER_PAGE)}
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() =>
                                setCurrentPage((prev) => Math.max(prev - 1, 1))
                              }
                              disabled={currentPage === 1}
                              className="p-1.5 rounded-lg border border-gray-100 text-gray-400 hover:text-gray-900 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                            >
                              <ChevronLeft size={14} />
                            </button>
                            <button
                              onClick={() =>
                                setCurrentPage((prev) =>
                                  Math.min(
                                    prev + 1,
                                    Math.ceil(
                                      questions.length / QUESTIONS_PER_PAGE,
                                    ),
                                  ),
                                )
                              }
                              disabled={
                                currentPage ===
                                Math.ceil(questions.length / QUESTIONS_PER_PAGE)
                              }
                              className="p-1.5 rounded-lg border border-gray-100 text-gray-400 hover:text-gray-900 hover:bg-gray-50 disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                            >
                              <ChevronRight size={14} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-6 animate-slideIn">
                      <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm relative overflow-hidden group">
                        <div className="absolute top-0 right-0 w-48 h-48 bg-blue-50 rounded-full -mr-24 -mt-24 opacity-50 blur-2xl group-hover:bg-blue-100 transition-colors duration-700"></div>

                        <div className="relative z-10">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-8 h-8 bg-gray-900 rounded-lg flex items-center justify-center text-white shadow-md">
                              <Sparkles size={16} />
                            </div>
                            <div>
                              <h3 className="text-sm font-bold tracking-wide text-gray-900 uppercase">
                                AI Idea Lab
                              </h3>
                              <p className="text-gray-400 font-medium text-[11px]">
                                Brainstorm context-aware questions from your
                                data
                              </p>
                            </div>
                          </div>

                          <p className="text-gray-500 font-medium text-[11px] mb-4 max-w-xl leading-relaxed">
                            Enter a seed question or topic. Our AI will
                            brainstorm{" "}
                            <span className="text-gray-900 font-bold">
                              3 distinct variations
                            </span>{" "}
                            a user might ask, then help you craft the perfect
                            logic for each.
                          </p>

                          <div className="flex gap-3">
                            <div className="flex-1 relative group/input">
                              <Search
                                size={14}
                                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within/input:text-gray-900 transition-colors"
                              />
                              <input
                                type="text"
                                value={newQuestion}
                                onChange={(e) => setNewQuestion(e.target.value)}
                                placeholder="e.g. Order Tracking or Return Policy"
                                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:border-slate-300 focus:bg-white transition-all outline-none shadow-sm"
                              />
                            </div>
                            <button
                              onClick={handleGenerateVariations}
                              disabled={isGenerating || !newQuestion.trim()}
                              className="px-4 py-2 bg-gray-900 text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-gray-800 transition-all flex items-center gap-1.5 shadow-md disabled:opacity-50 active:scale-95 flex-shrink-0"
                            >
                              {isGenerating ? (
                                <Sparkles size={12} className="animate-spin" />
                              ) : (
                                <Zap size={12} />
                              )}
                              Generate
                            </button>
                          </div>
                        </div>
                      </div>

                      {recommendedVariations.length > 0 && (
                        <div className="space-y-4 animate-slideUp">
                          <div className="flex items-center gap-2">
                            <div className="w-1 h-5 bg-gray-900 rounded-full"></div>
                            <h4 className="text-sm font-bold tracking-wide text-gray-900 uppercase">
                              AI Brainstorm Results
                            </h4>
                          </div>

                          <div className="grid grid-cols-1 gap-4">
                            {recommendedVariations.map((v, i) => (
                              <div
                                key={i}
                                className="bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm hover:shadow-md hover:shadow-gray-100/30 transition-all duration-500"
                              >
                                <div className="p-5">
                                  <div className="mb-4">
                                    <label className="text-[10px] font-bold text-gray-400 uppercase mb-1.5 block">
                                      Question Variation (AI-Generated)
                                    </label>
                                    <input
                                      type="text"
                                      value={v.question_text}
                                      onChange={(e) =>
                                        handleUpdateVariationInput(
                                          i,
                                          "question_text",
                                          e.target.value,
                                        )
                                      }
                                      className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-bold text-gray-900 focus:ring-2 focus:ring-gray-900 outline-none transition-all"
                                    />
                                  </div>

                                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                      <label className="text-[9px] font-black text-gray-400 uppercase tracking-wider ml-1 block">
                                        Variation Logic
                                      </label>
                                      <div className="relative group/area">
                                        <textarea
                                          value={v.instruction_text}
                                          onChange={(e) =>
                                            handleUpdateVariationInput(
                                              i,
                                              "instruction_text",
                                              e.target.value,
                                            )
                                          }
                                          placeholder="How should the AI respond to this variant?"
                                          className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 font-bold text-xs leading-relaxed h-20 focus:ring-2 focus:ring-gray-900 outline-none resize-none transition-all group-hover/area:bg-white"
                                        />
                                        {/* AI Improve button removed */}
                                      </div>
                                    </div>
                                    <div className="space-y-2">
                                      <label className="text-[9px] font-black text-gray-400 uppercase tracking-wider ml-1 block">
                                        Trigger Condition
                                      </label>
                                      <div className="relative">
                                        <input
                                          type="text"
                                          value={v.trigger_condition}
                                          onChange={(e) =>
                                            handleUpdateVariationInput(
                                              i,
                                              "trigger_condition",
                                              e.target.value,
                                            )
                                          }
                                          placeholder="e.g. When the specific order status is requested"
                                          className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-gray-900 font-bold text-xs focus:ring-2 focus:ring-gray-900 outline-none transition-all leading-normal"
                                        />
                                        <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                                          <Zap size={14} />
                                        </div>
                                      </div>
                                      <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 flex items-start gap-2">
                                        <Info
                                          size={12}
                                          className="text-gray-400 mt-0.5 shrink-0"
                                        />
                                        <p className="text-[11px] font-medium text-gray-600 leading-normal">
                                          "Triggers ensure the AI only uses this
                                          variation when relevant."
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className="flex bg-gray-50/50 border-t border-gray-100 p-1.5 gap-1.5">
                                  <button
                                    onClick={() => handleApproveVariation(v, i)}
                                    className="flex-1 py-2 bg-white hover:bg-gray-900 hover:text-white text-gray-900 rounded-xl font-bold text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-1.5 group/save border border-gray-200 shadow-sm"
                                  >
                                    <Check
                                      size={14}
                                      className="group-hover/save:scale-110 transition-transform"
                                    />
                                    Approve & Train
                                  </button>
                                  <button
                                    onClick={() =>
                                      setRecommendedVariations((prev) =>
                                        prev.filter((_, idx) => idx !== i),
                                      )
                                    }
                                    className="flex-1 py-2 bg-white hover:bg-red-500 hover:text-white text-red-500 rounded-xl font-bold text-xs uppercase tracking-widest transition-all flex items-center justify-center gap-1.5 group/reject border border-gray-100 shadow-sm"
                                  >
                                    <Trash2
                                      size={14}
                                      className="group-hover/reject:scale-110 transition-transform"
                                    />
                                    Discard
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {isGenerating && (
                        <div className="flex flex-col items-center justify-center py-12 animate-pulse">
                          <div className="w-10 h-10 border-4 border-gray-100 border-t-gray-900 rounded-full animate-spin mb-4"></div>
                          <p className="text-gray-400 font-medium uppercase tracking-normal text-[11px]">
                            AI Brainstorming...
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center p-8 text-center">
                <h2 className="text-sm font-bold tracking-wide text-gray-900 uppercase">
                  Select a Topic
                </h2>
                <p className="text-gray-400 text-[11px] font-medium mt-1">
                  Choose a knowledge domain from the sidebar or build a new one to start training your assistant.
                </p>
                <button
                  onClick={() => setShowTopicModal(true)}
                  className="mt-4 px-5 py-2.5 bg-gray-900 text-white rounded-xl font-bold text-[10px] uppercase tracking-widest hover:bg-black transition-all shadow-md active:scale-95"
                >
                  Build New Topic
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Design Modal */}
      {showTopicModal &&
        ReactDOM.createPortal(
          <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-md flex items-center justify-center z-[9999] p-6 animate-fadeIn">
            <div className="bg-white rounded-3xl w-full max-w-lg p-8 shadow-2xl animate-scaleIn border border-gray-100 relative overflow-hidden">
              <div className="flex justify-between items-center mb-10">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 tracking-wide uppercase">
                    New Knowledge Domain
                  </h3>
                  <p className="text-gray-400 text-xs font-medium mt-1 uppercase tracking-normal">
                    Create specialized intelligence
                  </p>
                </div>
                <button
                  onClick={() => setShowTopicModal(false)}
                  className="p-3 bg-gray-50 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-2xl transition-all"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-8">
                <div className="space-y-3">
                  <label className="text-[10px] font-bold text-gray-400 uppercase ml-2 block">
                    Domain Name
                  </label>
                  <input
                    value={topicName}
                    onChange={(e) => setTopicName(e.target.value)}
                    placeholder="e.g. Refund Policy or Product FAQ"
                    className="w-full px-5 py-3 bg-gray-50 border-0 rounded-xl text-sm font-bold text-gray-900 focus:ring-4 focus:ring-gray-900/5 focus:bg-white outline-none transition-all placeholder:text-gray-300"
                  />
                </div>

                <div className="space-y-3">
                  <label className="text-[10px] font-bold text-gray-400 uppercase ml-2 block">
                    Objective (Optional)
                  </label>
                  <textarea
                    value={topicDesc}
                    onChange={(e) => setTopicDesc(e.target.value)}
                    placeholder="Describe the scope for the AI..."
                    className="w-full p-6 bg-gray-50 border-0 rounded-[28px] text-gray-900 font-medium text-base leading-relaxed h-32 focus:ring-4 focus:ring-gray-900/5 focus:bg-white outline-none resize-none transition-all"
                  />
                </div>

                <div className="flex justify-center mt-4">
                  <button
                    onClick={handleCreateTopic}
                    disabled={!topicName.trim()}
                    className="px-10 py-2.5 bg-gray-900 text-white rounded-xl font-bold text-xs uppercase tracking-widest hover:bg-gray-800 transition-all shadow-lg shadow-gray-200 active:scale-95 disabled:opacity-50"
                  >
                    Create Intelligence Domain
                  </button>
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
        title={`Delete ${deleteData.type === "topic" ? "Intelligence Hub" : deleteData.type === "question" ? "Neural Link" : "Synaptic Response"}?`}
        message={`This action will permanently Delete this ${deleteData.type} from the neural repository. Are you prepared to proceed?`}
      />
    </div>
  );
};

export default KnowledgeBase;
